import {
    MepDashboardEmissionsBySomeTypeItem, MepDashboardRequest, MepGetDashboardEmissionsBySomeTypeRequest,
    MepGetPointsOfInterestHeatmapRequest, MepDashboardPointOfInterestHeatmap, MepPortDetails,
    MepGetDashboardPointsOfInterestHeatmapConfig,
    EmissionTypeCategoryEnum,
    MepLookupItem
} from "../../../../../../../../models/mep/mep.models";
import { computedFrom } from "aurelia-binding";
import { MepService } from "../../../../../../../../services/mep/mep.service";
import * as Enumerable from "linq";
import { ChartLegendViewModel } from "../../../../common-vm/chart-legend-view-model";
import { MobileHelper } from "../../../../../../../../helpers/mobile-helper";
import { MepDashboardFilter } from "../mep-dashboard-filter/mep-dashboard-filter";
import { Guid } from "../../../../../../../../utilities/guid";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { MepOperationModeColors } from "../../../../../../../../constants/mep/mep.constants";
import { NumberFormatHelper } from "../../../../../../../../helpers/number-format-helper";
import { ConditionalDecimalPlacesValueConverter } from "../../../../converters/conditional-decimal-places-value-converter";
import { MepAbout } from "../../../mep-home/tabs/mep-about/mep-about";
import { AlphaNumericSortHelper } from "../../../../../../../../helpers/alpha-numeric-sort-helper";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { useView } from "aurelia-framework";
const am4core = await import("@amcharts/amcharts4/core");
const am4charts = await import("@amcharts/amcharts4/charts");
const leaflet = await import('leaflet');
const leafletHeatmap = (await import('leaflet-heatmap')).default;

export interface EmissionsBySomeTypeColumn {
    id: number;
    name: string;
}

export interface EmissionsBySomeTypeRow {
    id: number;
    rowName: string;
    cells: EmissionsBySomeTypeCell[];
}

export interface EmissionsBySomeTypeCell {
    column: EmissionsBySomeTypeColumn;
    item: MepDashboardEmissionsBySomeTypeItem;
}

@useView('views/pages/modules/mep/tabs/mep-dashboard/partials/mep-emissions-by-type/mep-emissions-by-type.html')
export class EmissionsByTypeTabViewModel {
    heatmapHasError: boolean = false;
    isHeatmapLoading: boolean = false;
    isEmissionsByTypeLoading: boolean = false;
    emissionsTabType: EmissionsByTypeTabEnum;
    tableViewModel: EmissionsByTypeTableViewModel;
    pieChartViewModel: EmissionsByTypePieChartViewModel;
    heatmapViewModel: EmissionsByTypeHeatmapViewModel;
    tableForPrintingViewModel: EmissionsByTypeTableForPrintingViewModel;
    emissionsData: MepDashboardEmissionsBySomeTypeItem[];
    showMap: boolean = false;
    title: string;
    isMepTrial: boolean = false;
    piechartdiv: HTMLDivElement;
    isSingleEmissionType: boolean = false;
    isGhgEmissions: boolean = true;

    changeEmissionTypesFilterFunc: (emissionTypeIds: number[]) => void;
    changePointOfInterestsFilterFunc: (pointOfInterestIds: number[]) => void;
    changeVesselTypesFilterFunc: (vesselTypeIds: number[]) => void;
    changeOperationModesFilterFunc: (operationModesIds: number[]) => void;
    changeCargoTypesFilterFunc: (cargoTypeIds: number[]) => void;
    changeCargoTypeGroupsFilterFunc: (cargoTypeGroups: string[]) => void;
    applyFilterFunc: () => void;

    @computedFrom("filterViewModel.selectedPointsOfInterest", "filterViewModel.selectedVesselTypeGroups", "filterViewModel.selectedOperationModes")
    get isVisible(): boolean {
        let collection: any[] = null;
        const lastRequest = this.filterViewModel.getLastRequest();
        if(!lastRequest) return false;
        switch (this.emissionsTabType) {
            case EmissionsByTypeTabEnum.PointOfInterest:
                collection = lastRequest.pointOfInterestIds;
                break;
            case EmissionsByTypeTabEnum.VesselType:
                collection = lastRequest.vesselTypeGroupIds;
                break;
            case EmissionsByTypeTabEnum.OperationMode:
                collection = lastRequest.operationModeIds;
                break;
        }

        return collection && collection.length != 1;
    }

    @computedFrom("tableForPrintingViewModel", "tableForPrintingViewModel.tableViewModels.length")
    get numberOfPagesToPrint(): number {
        if (!this.tableForPrintingViewModel || !this.tableForPrintingViewModel.tableViewModels)
            return 1;

        return this.tableForPrintingViewModel.tableViewModels.length;
    }

    constructor(emissionsTabType: EmissionsByTypeTabEnum, private filterViewModel: MepDashboardFilter,
        private mepService: MepService, private notificationService: NotificationClientService, public portId: number, private configService: ConfigService) {
        this.emissionsTabType = emissionsTabType;
        this.changeEmissionTypesFilterFunc = (emissionsTypeIds: number[]) => this.filterViewModel.changeEmissionTypesFilter(emissionsTypeIds);
        this.changePointOfInterestsFilterFunc = (pointOfInterestIds: number[]) => this.filterViewModel.changePointOfInterestsFilter(pointOfInterestIds);
        this.changeVesselTypesFilterFunc = (vesselTypeIds: number[]) => this.filterViewModel.changeVesselTypesFilter(vesselTypeIds);
        this.changeOperationModesFilterFunc = (operationModesIds: number[]) => this.filterViewModel.changeOperationModesFilter(operationModesIds);
        this.changeCargoTypesFilterFunc = (cargoTypeIds: number[]) => this.filterViewModel.changeCargoTypeFilter(cargoTypeIds);
        this.changeCargoTypeGroupsFilterFunc = (cargoTypeGroups: string[]) => this.filterViewModel.changeCargoTypeGroupFilter(cargoTypeGroups);
        this.applyFilterFunc = () => this.filterViewModel.applyAllFilters();

        switch (this.emissionsTabType) {
            case EmissionsByTypeTabEnum.PointOfInterest:
                this.title = "Emissions by Point of Interest";
                this.showMap = true;
                break;
            case EmissionsByTypeTabEnum.VesselType:
                this.title = "Emissions by Vessel Type";
                break;
            case EmissionsByTypeTabEnum.OperationMode:
                this.title = "Emissions by Operation Mode";
                break;
            default:
                this.title = "";
        }
    }

    getTabData(request: MepDashboardRequest, isForPrinting: boolean = false, printHeatmapDiv: HTMLDivElement = null) {
        return new Promise<string>((resolve, reject) => {

            if (this.filterViewModel.emissionTypeCategoryIds.length == 1) {
                this.isGhgEmissions = this.filterViewModel.emissionTypeCategoryIds[0] == EmissionTypeCategoryEnum.GhgEmissions;
            }

            this.isEmissionsByTypeLoading = true;

            const getMepEmissionsBySomeTypePromise = this.getMepEmissionsBySomeType(request);
            let heatmapPromise: Promise<any> = null;

            if (this.emissionsTabType == EmissionsByTypeTabEnum.PointOfInterest) {
                this.heatmapViewModel = new EmissionsByTypeHeatmapViewModel(this.mepService, this.notificationService, this.portId, request.year, this.configService);
                //promises.push(this.heatmapViewModel.getPointsOfInterestHeatmap(request)); // disabled so that we do now keep waiting for this since it take a long time and we do not want it to hog the dashboard
            }

            if (this.heatmapViewModel && (this.showMap || isForPrinting)) {
                heatmapPromise = this.loadHeatmap(request, isForPrinting, printHeatmapDiv);
            }

            const allPromises = [getMepEmissionsBySomeTypePromise];

            if (heatmapPromise) {
                allPromises.push(heatmapPromise);
            }

            // we want to resolve before so that the dashboard does not wait for the heatmap to load 
            getMepEmissionsBySomeTypePromise.then(() => {
                this.isEmissionsByTypeLoading = false;
                resolve(null);
            }).catch(() => {
                this.isEmissionsByTypeLoading = false;
                reject(`MEP ${this.title} - Error retrieving the data!`);
            });
        });
    }

    loadHeatmap(request: MepDashboardRequest, isForPrinting: boolean = false, printHeatmapDiv: HTMLDivElement = null) {
        return new Promise<string>((resolve, reject) => {
            const heatmapPromise = this.heatmapViewModel.getPointsOfInterestHeatmap(request, isForPrinting, printHeatmapDiv);
            if (heatmapPromise) {
                this.isHeatmapLoading = true;
                this.heatmapHasError = false;
                heatmapPromise
                    .then(() => {
                        this.isHeatmapLoading = false;
                        resolve(null);
                    })
                    .catch(() => {
                        this.isHeatmapLoading = false;
                        this.heatmapHasError = true;
                        reject(null);
                    });
            }
        });
    }

    getMepEmissionsBySomeType(request: MepDashboardRequest) {
        return new Promise((resolve, reject) => {
            const emissionsRequest: MepGetDashboardEmissionsBySomeTypeRequest = {
                emissionTypeIds: request.emissionTypeIds,
                operationModeIds: request.operationModeIds,
                pointOfInterestIds: request.pointOfInterestIds,
                cargoTypeIds: request.cargoTypeIds,
                portId: request.portId,
                vesselTypeGroupIds: request.vesselTypeGroupIds,
                vesselTypeIds: request.vesselTypeIds,
                year: request.year,
                secondaryTypeId: this.emissionsTabType,
                months: request.months
            }
            this.mepService.getMepEmissionsBySomeType(emissionsRequest).then(response => {
                this.emissionsData = response.results;
                this.isSingleEmissionType = request.emissionTypeIds.length == 1;
                this.initTabData();
                resolve(null);
            }).catch(() => {
                reject();
            });
        });
    }

    initTabData() {
        if (!this.emissionsData || this.emissionsData.length == 0)
            return;

        const emissionsDataFiltered = this.emissionsData.filter(x =>
            this.isGhgEmissions
                ? x.emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions
                : x.emissionTypeCategoryId == EmissionTypeCategoryEnum.CriteriaPollutants);

        if (this.isSingleEmissionType) {
            this.pieChartViewModel = new EmissionsByTypePieChartViewModel(this.applyFilterFunc, this.changePointOfInterestsFilterFunc,
                this.changeVesselTypesFilterFunc, this.changeOperationModesFilterFunc, this.emissionsTabType, emissionsDataFiltered.filter(x => x.emissionsTypeId > -1), this.configService);
            this.pieChartViewModel.initEmissionsByTypePieChart(this.piechartdiv);
        }
        else {
            this.tableViewModel = new EmissionsByTypeTableViewModel(this.applyFilterFunc, this.changeEmissionTypesFilterFunc, this.changePointOfInterestsFilterFunc,
                this.changeVesselTypesFilterFunc, this.changeOperationModesFilterFunc, this.changeCargoTypesFilterFunc, this.changeCargoTypeGroupsFilterFunc, this.emissionsTabType, emissionsDataFiltered, null);
            this.tableViewModel.initTable();
        }
    }

    clearEmissionsData() {
        this.emissionsData = [];
        this.tableViewModel = null;
        this.pieChartViewModel = null;
        if (this.heatmapViewModel)
            this.heatmapViewModel.clearAllMaps();
        this.heatmapViewModel = null;
    }

    toggleHeatmap() {
        if (this.heatmapViewModel && (!this.heatmapViewModel.heatmap || !this.heatmapViewModel.heatmap.map) && !this.showMap) {
            const request = this.filterViewModel.getLastRequest();
            this.loadHeatmap(request);
        }

        this.showMap = !this.showMap;
    }

    setIsGhgEmission = (value: boolean) => {
        this.isGhgEmissions = value;
        this.initTabData();
    }
}

export class EmissionsByTypeTableViewModel {
    columns: EmissionsBySomeTypeColumn[];
    rows: EmissionsBySomeTypeRow[];
    subtitle: string;

    setEmissionTypesFilterFunc: (emissionTypeIds: number[]) => void;
    setPointOfInterestsFilterFunc: (pointOfInterestIds: number[]) => void;
    setVesselTypesFilterFunc: (vesselTypeIds: number[]) => void;
    setOperationModesFilterFunc: (operationModesIds: number[]) => void;
    setCargoTypesFilterFunc: (cargoTypesIds: number[]) => void;
    setCargoTypeGroupsFilterFunc: (cargoTypeGroups: string[]) => void;
    applyFilterFunc: () => void;

    constructor(applyFilterFunc: () => void,
        setEmissionTypesFilterFunc: (emissionTypeIds: number[]) => void,
        setPointOfInterestsFilterFunc: (pointOfInterestIds: number[]) => void,
        setVesselTypesFilterFunc: (vesselTypeIds: number[]) => void,
        setOperationModesFilterFunc: (operationModesIds: number[]) => void,
        setCargoTypesFilterFunc: (cargoTypesIds: number[]) => void,
        setCargoTypeGroupsFilterFunc: (cargoTypeGroups: string[]) => void,
        private emissionsTabType: EmissionsByTypeTabEnum, 
        private emissionsData: MepDashboardEmissionsBySomeTypeItem[],
        private allEmissionsData: MepDashboardEmissionsBySomeTypeItem[]) {
        this.applyFilterFunc = applyFilterFunc;
        this.setEmissionTypesFilterFunc = setEmissionTypesFilterFunc;
        this.setPointOfInterestsFilterFunc = setPointOfInterestsFilterFunc;
        this.setVesselTypesFilterFunc = setVesselTypesFilterFunc;
        this.setOperationModesFilterFunc = setOperationModesFilterFunc;
        this.setCargoTypesFilterFunc = setCargoTypesFilterFunc;
        this.setCargoTypeGroupsFilterFunc = setCargoTypeGroupsFilterFunc;
    }

    initTable() {
        this.columns = Enumerable.from(this.emissionsData).select(x => {
            return {
                id: x.emissionsTypeId,
                name: x.emissionsTypeName
            }
        }).distinct(x => x.id).toArray();
        const orderedEmissionData = this.emissionsData.sort((a, b) => AlphaNumericSortHelper.alphaNumericSort(a, b, 'secondaryTypeName'));
        this.rows = Enumerable.from(orderedEmissionData).select((x): EmissionsBySomeTypeRow => {
            return {
                id: x.secondaryTypeId,
                rowName: x.secondaryTypeName,
                cells: Enumerable.from(this.columns).select(col => {
                    return {
                        column: col,
                        item: Enumerable.from(this.emissionsData)
                            .where(y => y.emissionsTypeId == col.id && y.secondaryTypeId == x.secondaryTypeId)
                            .firstOrDefault()
                    }
                }).toArray()
            }
        }).distinct(x => x.id).toArray();
    }

    getEmissionsBubbleSize(item: MepDashboardEmissionsBySomeTypeItem, maxValue: number = null, maxSize: number = 72) {
        const maxSizeInPixels = maxSize;
        const minSizeInPixels = 3;
        const value = Math.sqrt(item?.totalEmissionsQtyTonnes / 2);
        const data = this.allEmissionsData ? this.allEmissionsData : this.emissionsData;

        if (maxValue == null) {
            maxValue = Enumerable.from(data).select(x => Math.sqrt(x.totalEmissionsQtyTonnes / 2)).max();
        }

        const valuePercentage = value / maxValue;
        const size = maxSizeInPixels * valuePercentage;

        if (value > 0 && size < minSizeInPixels) {
            return minSizeInPixels;
        }

        return size;
    }

    applyFilter(item: MepDashboardEmissionsBySomeTypeItem) {
        //A total was selected
        if (item.emissionsTypeId == -1) {
            this.setEmissionTypesFilterFunc(Enumerable.from(this.emissionsData).select(x => x.emissionsTypeId).distinct().toArray());
        } else {
            this.setEmissionTypesFilterFunc([item.emissionsTypeId]);
        }
        switch (this.emissionsTabType) {
            case EmissionsByTypeTabEnum.PointOfInterest:
                this.setPointOfInterestsFilterFunc([item.secondaryTypeId]);
                break;
            case EmissionsByTypeTabEnum.VesselType:
                this.setVesselTypesFilterFunc([item.secondaryTypeId]);
                break;
            case EmissionsByTypeTabEnum.OperationMode:
                this.setOperationModesFilterFunc([item.secondaryTypeId]);
                break;
            case EmissionsByTypeTabEnum.CargoTypeGroup:
                this.setCargoTypeGroupsFilterFunc([item.secondaryTypeName]);
                break;
            case EmissionsByTypeTabEnum.CargoType:
                this.setCargoTypesFilterFunc([item.secondaryTypeId]);
                break;
        }
        this.applyFilterFunc();
    }
}

export class EmissionsByTypePieChartViewModel {
    emissionsByTypePieChart: any;
    emissionsByTypePieChartLegend: ChartLegendViewModel;

    setPointOfInterestsFilterFunc: (pointOfInterestIds: number[]) => void;
    setVesselTypesFilterFunc: (vesselTypeIds: number[]) => void;
    setOperationModesFilterFunc: (operationModesIds: number[]) => void;
    applyFilterFunc: () => void;

    constructor(applyFilterFunc: () => void, setPointOfInterestsFilterFunc: (pointOfInterestIds: number[]) => void,
        setVesselTypesFilterFunc: (vesselTypeIds: number[]) => void, setOperationModesFilterFunc: (operationModesIds: number[]) => void,
        private emissionsTabType: EmissionsByTypeTabEnum, private emissionsData: MepDashboardEmissionsBySomeTypeItem[],
        private configService: ConfigService) {
        this.applyFilterFunc = applyFilterFunc;
        this.setPointOfInterestsFilterFunc = setPointOfInterestsFilterFunc;
        this.setVesselTypesFilterFunc = setVesselTypesFilterFunc;
        this.setOperationModesFilterFunc = setOperationModesFilterFunc;
    }

    initEmissionsByTypePieChart(divElement: HTMLDivElement) {
        if (!divElement)
            return;

        //Create chart instance
        am4core.addLicense(this.configService.config.settings.amChartsLicense);
        this.emissionsByTypePieChart = am4core.create(divElement, am4charts.PieChart);
        this.emissionsByTypePieChart.innerRadius = 100;

        //create series
        const pieSeries = this.emissionsByTypePieChart.series.push(new am4charts.PieSeries());
        pieSeries.dataFields.value = "totalEmissionsQtyTonnes";
        pieSeries.dataFields.percentage = "totalEmissionsPercentage"
        pieSeries.dataFields.category = "secondaryTypeName";

        pieSeries.slices.template.stroke = new am4core.InterfaceColorSet().getFor("background");
        pieSeries.slices.template.strokeWidth = 2;
        pieSeries.slices.template.strokeOpacity = 1;

        const slice = pieSeries.slices.template;
        slice.states.getKey("hover").properties.scale = 1.1;
        slice.states.getKey("active").properties.shiftRadius = 0;

        pieSeries.labels.template.text = "{percentage.formatNumber('#.')}%";
        pieSeries.labels.template.fill = am4core.color("#42526E");
        pieSeries.tooltip.disabled = true;

        pieSeries.labels.template.adapter.add("text", (text, target) => {
            if (target.dataItem && (target.dataItem.values.value.percent == 0)) {
                return "";
            } else if (target.dataItem && (target.dataItem.values.value.percent < 1)) {
                return "<1%";
            }
            else {
                return text;
            }
        });

        //Add the data
        this.emissionsByTypePieChart.data = this.emissionsData;

        this.emissionsByTypePieChartLegend = new ChartLegendViewModel();
        const legendData = Enumerable.from(this.emissionsData).select(x => {
            return {
                id: x.secondaryTypeId,
                name: x.secondaryTypeName
            }
        })

        switch (this.emissionsTabType) {
            case EmissionsByTypeTabEnum.VesselType:
                this.emissionsByTypePieChartLegend.legendItems = Enumerable.from(this.emissionsData).select(x => {
                    return {
                        id: x.secondaryTypeId,
                        name: x.secondaryTypeName,
                        color: x.hashColour
                    }
                }).toArray();
                break;
            case EmissionsByTypeTabEnum.OperationMode:
                this.emissionsByTypePieChartLegend.legendItems = MepOperationModeColors.filter(x => legendData.where(y => y.id == x.id).any());
                break;
            default:
                this.emissionsByTypePieChartLegend.generateLegendItems(legendData.toArray());
        }

        const colorSet = new am4core.ColorSet();
        colorSet.list = Enumerable.from(this.emissionsByTypePieChartLegend.legendItems).select(x => x.color).toArray().map(function (color) {
            return am4core.color(color);
        });
        pieSeries.colors = colorSet;
    }

    emissionsByTypeCardColor(id: number) {
        if (!this.emissionsByTypePieChartLegend)
            return null;
        const result = this.emissionsByTypePieChartLegend.getColor(id);
        return result;
    }

    applyFilter(item: MepDashboardEmissionsBySomeTypeItem) {
        switch (this.emissionsTabType) {
            case EmissionsByTypeTabEnum.PointOfInterest:
                this.setPointOfInterestsFilterFunc([item.secondaryTypeId]);
                break;
            case EmissionsByTypeTabEnum.VesselType:
                this.setVesselTypesFilterFunc([item.secondaryTypeId]);
                break;
            case EmissionsByTypeTabEnum.OperationMode:
                this.setOperationModesFilterFunc([item.secondaryTypeId]);
                break;
        }
        this.applyFilterFunc();
    }
}

export class EmissionsByTypeHeatmapViewModel {
    public heatmap: HeatmapViewModel;
    public heatmapPrint: HeatmapViewModel;
    public heatmapData: MepDashboardPointOfInterestHeatmap[];
    private configs: MepGetDashboardPointsOfInterestHeatmapConfig[]
    private heatmapSettings: any = {
        mapName: "heatmap",
        minElementId: "#min",
        maxElementId: "#max",
        gradientElementId: "#gradient"
    }
    private heatmapPrintSettings: any = {
        mapName: "heatmapprint",
        minElementId: "#minprint",
        maxElementId: "#maxprint",
        gradientElementId: "#gradientprint"
    }

    constructor(private mepService: MepService, private notificationClientService: NotificationClientService, 
        private portId: number, private year: number, private configService: ConfigService) {
    }

    lastRequestId: string;// this is used since we do not wait for this request to be resolved in the dashboard, thus we want to ignore a response if it is not the latest in case a user does more requests
    getPointsOfInterestHeatmap(request: MepDashboardRequest, isForPrinting: boolean, printHeatmapDiv: HTMLDivElement) {
        this.lastRequestId = Guid.generateGuid();
        return new Promise((resolve, reject) => {
            const heatmapRequest: MepGetPointsOfInterestHeatmapRequest = {
                emissionTypeIds: request.emissionTypeIds,
                operationModeIds: request.operationModeIds,
                pointOfInterestIds: request.pointOfInterestIds,
                cargoTypeIds: request.cargoTypeIds,
                portId: request.portId,
                vesselTypeGroupIds: request.vesselTypeGroupIds,
                vesselTypeIds: request.vesselTypeIds,
                zoomLevel: this.heatmap && this.heatmap.map ? this.heatmap.map.getZoom() : 0,
                year: request.year,
                months: request.months,
                requestId: this.lastRequestId
            };
            this.mepService.getMepDashboardPointsOfInterestHeatmap(heatmapRequest).then(response => {
                if (response.hasError) {
                    reject();
                } else {
                    if (this.lastRequestId == response.requestId) { // only apply the data if this is latest request (to prevent showing incorrect data based on the current filter)
                        this.heatmapData = response.results;
                        this.configs = response.heatmapConfigs;
                        this.initializeHeatmap(isForPrinting, printHeatmapDiv);
                    } else {
                        console.log(`Heatmap request ignore last-request-id:${this.lastRequestId} | response-request-id: ${response.requestId}`)
                    }

                    resolve(null);
                }
            }).catch(() => {
                reject()
            });
        });
    }

    initializeHeatmap(isForPrinting: boolean, printHeatmapDiv: HTMLDivElement) {

        if (isForPrinting) {
            if (!this.heatmapPrint) {
                this.heatmapPrint = new HeatmapViewModel(this.heatmapPrintSettings, this.mepService, this.notificationClientService, this.portId, this.year, this.heatmapData, this.configs, this.configService);
            }
            this.heatmapPrint.initializeHeatmap(printHeatmapDiv);
        }
        else {
            if (!this.heatmap) {
                this.heatmap = new HeatmapViewModel(this.heatmapSettings, this.mepService, this.notificationClientService, this.portId, this.year, this.heatmapData, this.configs, this.configService);
            }
            this.heatmap.initializeHeatmap(null);
        }
    }

    clearAllMaps() {
        this.clearMap();
        this.clearPrintMap();
    }

    clearMap() {
        if (this.heatmap) {
            this.heatmap.clearMap();
        }
    }

    clearPrintMap() {
        if (this.heatmapPrint) {
            this.heatmapPrint.clearMap();
            this.heatmapPrint = null;
        }
    }
}

export class HeatmapViewModel {
    private getMap: Promise<MepPortDetails> = null;
    private heatmapSettings: any;
    scientificNotationConverter: ConditionalDecimalPlacesValueConverter = new ConditionalDecimalPlacesValueConverter();
    map: any;

    constructor(heatmapSettings: any, private mepService: MepService, private notificationClientService: NotificationClientService, portId: number, year: number,
        private heatmapData: MepDashboardPointOfInterestHeatmap[],
        private configs: MepGetDashboardPointsOfInterestHeatmapConfig[], private configService: ConfigService) {
        this.getMap = this.mepService.getMepPortDetails(portId, year, true);
        this.heatmapSettings = heatmapSettings;
    }

    initializeHeatmap(heatmapDiv: HTMLDivElement) {
        if (this.map)
            return;

        this.getMap.then(portDetails => {
            const kgToTonns = 1000;
            const isMobile = MobileHelper.isMobileDevice();
            const mapElement = heatmapDiv ? heatmapDiv : this.heatmapSettings.mapName;
            if (!heatmapDiv && !document.getElementById(mapElement))
                return;
            this.clearMap();
            this.map = leaflet.map(mapElement, { center: [51.505, -0.09], zoom: 13, dragging: !isMobile, tap: !isMobile, gestureHandling: true });
            const map = this.map;

            const config = this.configs && this.configs.length > 0 ? this.configs[0] : null;

            leaflet.tileLayer(this.configService.config.urls.leafletMapTilesUrl, {
                attribution: 'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
                maxZoom: 20,
                id: 'mapbox/streets-v11',
                tileSize: 512,
                zoomOffset: -1,
                accessToken: this.configService.config.settings.leafletAccessToken
            }).addTo(this.map);
            leaflet.control.scale().addTo(this.map);

            /*  start legend code */
            // we want to display the gradient, so we have to draw it
            let legendMaxValue = 0;
            let legendMinValue = 0;
            if (this.heatmapData && this.heatmapData.length > 0) {
                legendMaxValue = Enumerable.from(this.heatmapData).max(x => x.intensityValue);
                legendMinValue = Enumerable.from(this.heatmapData).min(x => x.intensityValue);
            }
            const convertedMaxValue = legendMaxValue ? legendMaxValue / kgToTonns : 0;
            const formattedMaxValue = this.scientificNotationConverter.toView(convertedMaxValue, null);
            const convertedMinValue = legendMinValue ? legendMinValue / kgToTonns : 0;

            const legendCanvas = document.createElement('canvas');
            legendCanvas.width = 100;
            legendCanvas.height = 10;
            const min = document.querySelector(this.heatmapSettings.minElementId);
            const max = document.querySelector(this.heatmapSettings.maxElementId);
            const gradientImg: HTMLImageElement = document.querySelector(this.heatmapSettings.gradientElementId);
            const legendCtx = legendCanvas.getContext('2d');
            let gradientCfg: any;

            function updateLegend(data) {
                // the onExtremaChange callback gives us min, max, and the gradientConfig
                // so we can update the legend
                min.innerHTML = !convertedMinValue ? 0 : convertedMinValue < 1 ? '< 1' : NumberFormatHelper.getFormattedValue(convertedMinValue, null, 2);
                max.innerHTML = formattedMaxValue;
                // regenerate gradient image
                if (data.gradient != gradientCfg) {
                    gradientCfg = data.gradient;
                    const gradient = legendCtx.createLinearGradient(0, 0, 100, 1);
                    for (const key in gradientCfg) {
                        gradient.addColorStop(Number(key), gradientCfg[key]);
                    }
                    legendCtx.fillStyle = gradient;
                    legendCtx.fillRect(0, 0, 100, 10);
                    gradientImg.src = legendCanvas.toDataURL();
                }
            }
            /* legend code end */

            const cfg = {
                // radius should be small ONLY if scaleRadius is true (or small radius is intended)
                // if scaleRadius is false it will be the constant radius used in pixels
                "radius": config ? config.radius : 20,
                "minOpacity": config ? config.minOpacity : 0.0,
                "maxOpacity": config ? config.maxOpacity : 0.8,
                // "opacity": 0.8,
                // scales the radius based on map zoom
                "scaleRadius": config ? config.scaleRadius : false,
                // if set to false the heatmap uses the global maximum for colorization
                // if activated: uses the data maximum within the current map boundaries
                //   (there will always be a red spot with useLocalExtremas true)
                "useLocalExtrema": config ? config.useLocalExtrema : false,
                // which field name in your data represents the latitude - default "lat"
                latField: 'latitude',
                // which field name in your data represents the longitude - default "lng"
                lngField: 'longitude',
                // which field name in your data represents the data value - default "value"
                valueField: 'intensityValue',
                // gradient: {
                //     0.45: "rgb(0,0,255)",
                //     0.55: "rgb(0,255,255)",
                //     0.65: "rgb(0,255,0)",
                //     0.95: "yellow",
                //     1.0: "rgb(255,0,0)"
                // }
                onExtremaChange: function (data) {
                    updateLegend(data);
                }
            };

            //we need to to get the max value for the first time to initialize the heatmap properly
            const zoomLevel = Enumerable.from(this.configs).orderByDescending(x => x.zoomLevel).firstOrDefault(x => x.zoomLevel <= this.map.getZoom());
            let maxValue: number = zoomLevel ? zoomLevel.max : map.getZoom();
            //we need to use local variable to be able to use it within the function below
            const localConfigs = this.configs;
            //when we zoom we need to update the radius for the heatmap dots 
            this.map.on('zoomend', function () {
                const currZoom = map.getZoom();
                console.log('zoom finished - ' + currZoom);
                const configToUse = Enumerable.from(localConfigs).orderByDescending(x => x.zoomLevel).firstOrDefault(x => x.zoomLevel <= currZoom);
                if (configToUse) {
                    cfg.radius = configToUse.radius;
                    cfg.scaleRadius = configToUse.scaleRadius;
                    cfg.useLocalExtrema = configToUse.useLocalExtrema;
                    cfg.minOpacity = configToUse.minOpacity;
                    cfg.maxOpacity = configToUse.maxOpacity;
                    maxValue = configToUse.max;
                }
            });

            this.map.options.minZoom = this.configs.length > 0 ? Enumerable.from(this.configs).min(x => x.zoomLevel) : 0;
            this.map.options.maxZoom = this.configs.length > 0 ? Enumerable.from(this.configs).max(x => x.zoomLevel) : maxValue;

            const geoJsonPortArea = MepAbout.createPortArea(this.map, portDetails);
            MepAbout.createPointsOfInterest(this.map, portDetails);

            const heatmapLayer = new leafletHeatmap(cfg);
            heatmapLayer.addTo(this.map);

            const data = {
                max: maxValue,
                min: 0,
                data: this.heatmapData
            }
            heatmapLayer.setData(data);

            this.map.fitBounds(geoJsonPortArea.getBounds());

        })
            .catch(e => {
                console.error(e);
                if (this.map) this.notificationClientService.show("MEP Error", "Error loading port details!", NotificationType.Error);
            });

    }

    clearMap() {
        if (this.map) {
            this.map.off();
            this.map = this.map.remove();
        }
    }
}

export class EmissionsByTypeTableForPrintingViewModel {
    tabTypeId: number;
    tableViewModels: EmissionsByTypeTableViewModel[];
    maxValue: number;
    
    constructor(emissionsTabType: number, private selectedEmissionTypes: MepLookupItem[], private emissionsData: MepDashboardEmissionsBySomeTypeItem[]) {
        this.tabTypeId = emissionsTabType;
        this.initTabViewModelForPrinting();
    }

    initTabViewModelForPrinting() {
        const maxColumnSize = 9;
        this.tableViewModels = [];
        const emissionTypeCategoryIds = Enumerable.from(this.selectedEmissionTypes)
            .where(x => x.additionalData != null)
            .select(x => x.additionalData.emissionTypeCategoryId)
            .distinct()
            .toArray();

        emissionTypeCategoryIds.forEach(emissionTypeCategoryId => {
            const emissionData = this.emissionsData.filter(x => x.emissionTypeCategoryId == emissionTypeCategoryId);
            const emissionTypes = Enumerable.from(emissionData).select(x => x.emissionsTypeId).distinct(x => x).toArray();
            const totalColumnsNumber = Enumerable.from(emissionTypes).count();
            const numberOfTablesForColumns = Math.ceil(totalColumnsNumber / maxColumnSize);

            const maxRowsSize = 8;
            const rowTypes = Enumerable.from(emissionData).select(x => x.secondaryTypeId).distinct(x => x).toArray();
            const totalRowNumber = Enumerable.from(rowTypes).count();
            const numberOfTablesForRows = Math.ceil(totalRowNumber / maxRowsSize);

            for (let i = 0; i < numberOfTablesForColumns; i++) {
                const currentTableEmissionTypes = Enumerable.from(emissionTypes).skip(i * maxColumnSize).take(maxColumnSize).toArray();

                for (let j = 0; j < numberOfTablesForRows; j++) {
                    const currentTableRowTypes = Enumerable.from(rowTypes).skip(j * maxRowsSize).take(maxRowsSize).toArray();
                    const currentTableEmissionsData = Enumerable.from(emissionData)
                        .where(x => currentTableEmissionTypes.indexOf(x.emissionsTypeId) >= 0 && currentTableRowTypes.indexOf(x.secondaryTypeId) >= 0).toArray();

                    if (!currentTableEmissionsData || currentTableEmissionsData.length == 0)
                        continue;

                    const tableViewModel = new EmissionsByTypeTableViewModel(null, null, null, null, null, null, null, this.tabTypeId, currentTableEmissionsData, null);
                    tableViewModel.subtitle = emissionTypeCategoryId == EmissionTypeCategoryEnum.CriteriaPollutants ? "Criteria Pollutants" : "GHG Emissions";
                    tableViewModel.initTable();
                    this.tableViewModels.push(tableViewModel);
                }
            }
        });

        this.maxValue = Enumerable.from(this.emissionsData).select(x => x.smoothenedEmissionProportion).max();
    }
}

export enum EmissionsByTypeTabEnum {
    PointOfInterest = 1,
    VesselType = 2,
    OperationMode = 3,
    CargoType = 4,
    CargoTypeGroup = 5
}